import { useEffect, useRef } from 'react';

import { StagingPreviewNavigateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/staging-preview-navigation-commands';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';

/**
 * Lets the Rovo staging area swap the previewed content over the bridge instead of reloading the
 * iframe. Uses `replace` to prevent preview navigation from growing the top-level browser history.
 *
 * `clientId` is the bridge client the content already registers (e.g. the page editor's or the
 * whiteboard's); only the handler is added and removed here, the content owns the client's
 * readiness and teardown. `onBeforeRedirect` runs before the route change (e.g. to persist pending
 * edits); if it rejects, the route change still happens.
 */
export const useStagingNavigationHost = ({
	enabled,
	clientId,
	onBeforeRedirect,
}: {
	enabled: boolean;
	clientId: ClientId;
	onBeforeRedirect?: () => Promise<void>;
}): void => {
	const { replace } = useRouteActions();
	// Ref avoids re-installing bridge handler when the callback identity changes
	const onBeforeRedirectRef = useRef(onBeforeRedirect);
	onBeforeRedirectRef.current = onBeforeRedirect;
	const latestUrlRef = useRef<string | null>(null);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		const bridge = getBrowserBridgeAPI();
		bridge.on(clientId, StagingPreviewNavigateCommand, async ({ url }) => {
			latestUrlRef.current = url;
			try {
				await onBeforeRedirectRef.current?.();
			} catch {
				// Edits are still unsaved; the content's own unsaved-changes guard applies on the route change.
			}
			// Only navigate to the latest in-flight command
			if (latestUrlRef.current !== url) {
				return false;
			}
			replace(url);
			return true;
		});

		return () => bridge.off(clientId, StagingPreviewNavigateCommand);
	}, [enabled, clientId, replace]);
};
